<script lang="ts">
	/**
	 * Slide-down navigation for narrow viewports.
	 *
	 * Mirrors the desktop nav rather than defining its own link list, so the two
	 * cannot drift apart. That mirroring is the reason FAQ appears here: it is in
	 * the desktop utility bar, and this menu is the only navigation a phone gets.
	 */
	import { slide } from 'svelte/transition';
	import { prefersReducedMotion } from 'svelte/motion';
	import { m } from '$lib/paraglide/messages';
	import { reloadIfWordPress } from '$lib/wordpress-links';
	import { page } from '$app/state';
	import { getCategoriesContext } from '$lib/context';
	import { getLocale } from '$lib/paraglide/runtime';
	import { pagePath } from '$lib/pages';
	import { isActivePath } from '$lib/helpers/nav';
	import { DERIVED_CATEGORIES } from '$lib/data/derived-categories';
	// From `services/category-path`, not `services/categories`: the latter reaches
	// the catalogue and every strain body with it, and this component is in the
	// header of every page. See the note in that module.
	import { categoryPath } from '$lib/services/category-path';
	import { prefixPath, type Locale } from '$lib/i18n';
	import SearchBar from '../SearchBar.svelte';
	import SocialLinks from '../SocialLinks.svelte';
	import ThemeToggle from '../ThemeToggle.svelte';

	let { open = false, onclose }: { open?: boolean; onclose?: () => void } = $props();

	const categoriesOf = getCategoriesContext();
	const categories = $derived(categoriesOf());
	const currentPath = $derived(page.url.pathname);

	/** Same taxonomy and order as the desktop dropdown and the strains site. */
	const ORDER = [
		'indica-seeds', 'sativa-seeds', 'hybrid-seeds', 'feminised-seeds', 'autoflower-seeds',
		'cbd-seeds', 'high-thc-seeds', 'indoor-seeds', 'outdoor-seeds', 'fast-flowering-cannabis-seeds'
	];
	const ordered = $derived(
		ORDER.map((key) => {
			const match = categories.find((c) => c.key === key);
			if (match) return match;
			// Derived categories have no WooCommerce entry but a real page; their
			// labels live with the definition rather than being copied per menu.
			const derived = DERIVED_CATEGORIES.find((c) => c.slug === key);
			if (!derived) return undefined;
			const locale = getLocale() as Locale;
			return {
				key,
				slug: key,
				label: derived.label[locale],
				fullSlug: categoryPath(locale, key)
			};
		}).filter((c) => c !== undefined)
	);
	/** Same unfiltered listing the desktop dropdown heads its categories with. */
	const allSeedsHref = $derived(
		categories.find((c) => c.key === 'cannabis-seeds')?.fullSlug ??
			prefixPath('/shop', getLocale() as Locale)
	);
	// From layout data: only the server knows which locales have an archive.
	const blogHref = $derived((page.data.blogHref as string) ?? '/learn');
	const contactHref = $derived(pagePath('contact', getLocale()));
	const faqHref = $derived(pagePath('faq', getLocale()));
	const aboutHref = $derived(pagePath('about', getLocale()));
	const resellersHref = $derived(pagePath('resellers', getLocale()));

	/**
	 * Secondary links, filtered so an untranslated page is dropped rather than
	 * linked into a 404 — the same rule the utility bar and footer follow.
	 */
	const secondary = $derived(
		[
			{ href: contactHref, label: m.contact_us() },
			{ href: faqHref, label: m.faq() },
			{ href: aboutHref, label: m.about_us() },
			// WordPress-served, so never a "current page" here.
			{ href: '/my-account', label: m.sign_in() }
		].filter((link) => link.href !== undefined)
	);

	/**
	 * `py-2` is not decoration. These were bare `block` links with no vertical
	 * padding, which on a phone is a tap target a few pixels tall between two
	 * others — the sort of thing that opens the wrong category.
	 */
	const linkClass = (active: boolean) =>
		`block rounded-sm px-2 py-2 transition-colors ${
			active ? 'bg-surface-muted font-semibold text-brand-600' : 'text-body hover:bg-surface-muted'
		}`;

	// Honour the OS setting: the slide is decoration, and the menu must still open.
	const duration = $derived(prefersReducedMotion.current ? 0 : 200);
</script>

{#if open}
	<div class="border-t border-subtle bg-surface lg:hidden" transition:slide={{ duration }}>
		<nav class="px-6 py-4" aria-label="Mobile">
			<!--
				Search leads, because on a phone this menu is the only place it
				exists — the masthead field is `lg:` and up. `onSelect` closes the
				menu: `SearchBar` navigates on its own, and leaving the panel open
				over the page it just navigated to hides the result.
			-->
			<div class="mb-4">
				<SearchBar onSelect={onclose} />
			</div>

			<ul class="grid gap-1 text-sm">
				{#each ordered as category (category.key)}
					{@const active = isActivePath(currentPath, category.fullSlug)}
					<li>
						<a
							href={category.fullSlug}
							class={linkClass(active)}
							aria-current={active ? 'page' : undefined}
							onclick={onclose}
						>
							{category.label}
						</a>
					</li>
				{/each}

				<!-- Same treatment as the desktop dropdown: the way out of the taxonomy. -->
				<li class="mt-2 border-t border-subtle pt-3">
					<a
						href={allSeedsHref}
						class="flex items-center justify-between gap-6 rounded-sm px-2 py-2 font-semibold text-brand-500 transition-colors hover:bg-surface-muted"
						onclick={onclose}
					>
						{m.view_all_seeds()}
						<svg class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
							<path
								fill-rule="evenodd"
								d="M2 10a.75.75 0 01.75-.75h12.59l-2.1-1.95a.75.75 0 111.02-1.1l3.5 3.25a.75.75 0 010 1.1l-3.5 3.25a.75.75 0 11-1.02-1.1l2.1-1.95H2.75A.75.75 0 012 10z"
								clip-rule="evenodd"
							/>
						</svg>
					</a>
				</li>

				{#each [{ href: blogHref, label: m.news_and_blog() }, ...(resellersHref ? [{ href: resellersHref, label: m.resellers() }] : [])] as link (link.href)}
					{@const active = isActivePath(currentPath, link.href)}
					<li>
						<a
							href={link.href}
							class="{linkClass(active)} font-medium"
							aria-current={active ? 'page' : undefined}
							onclick={onclose}
						>
							{link.label}
						</a>
					</li>
				{/each}
			</ul>

			<hr class="my-4 border-subtle" />

			<ul class="grid gap-1 text-sm">
				{#each secondary as link (link.label)}
					{@const active = isActivePath(currentPath, link.href)}
					<li>
						<a
							href={link.href}
							data-sveltekit-reload={reloadIfWordPress(link.href)}
							class="block rounded-sm px-2 py-2 transition-colors hover:bg-surface-muted {active
								? 'font-semibold text-brand-600'
								: 'text-muted'}"
							aria-current={active ? 'page' : undefined}
							onclick={onclose}
						>
							{link.label}
						</a>
					</li>
				{/each}
			</ul>

			<!--
				Social and theme close the menu out, the same pairing the desktop
				utility bar carries at the top of the header — social on the left,
				the presentation control on the right. Both are reachable on a
				phone only from here: the utility bar is `lg:` and up, and the
				footer's social links are a full page-scroll away.

				`SocialLinks` reads `$lib/data/social`, so this is the same list as
				the utility bar and the footer and cannot drift from them.
			-->
			<div class="mt-4 flex items-end justify-between gap-4 border-t border-subtle pt-4">
				<div>
					<span class="mb-2 block text-xs font-semibold tracking-wide text-muted uppercase">
						{m.follow_us()}
					</span>
					<SocialLinks size="sm" />
				</div>

				<ThemeToggle />
			</div>
		</nav>
	</div>
{/if}
